import { useTranslation } from 'react-i18next'
import { FieldArray, Form, FormikProvider } from 'formik'
import styled from 'styled-components'

import {
  BottomGroupMolecule,
  PageContentAtom,
  PrimaryButtonAtom,
  SecondaryButtonAtom,
  SpacerAtom,
  ToolbarMolecule
} from '@justomx/atlas-components'

import { CardImageMolecule } from 'components/molecules/CardImage.molecule'
import { LotFormOrganism } from 'components/organisms/LotForm.organism'
import { useProductLotsData } from './ProductLots.hooks'
import { MissingDialogOrganism } from 'components/organisms/MissingDialog.organism'
import LoaderAtom from 'components/atoms/Loader.atom'
import { labelsByMeasureUnit } from './ProductLots.helpers'
import { CounterCardAtom } from 'components/atoms/CounterCard.atom'
import { MEASURE_UNITS, RECEIPTS } from 'utils/constants'

const FormContent = styled(PageContentAtom)`
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const ProductLotsPage = () => {
  const { t } = useTranslation('global')
  const {
    product,
    formConfig,
    boxesCounter,
    missingDialogRef,
    loading,
    container,
    missingCounter,
    expectedQuantity,
    receiptType,
    newTransfersFlow,
    printerReady,
    actions
  } = useProductLotsData()

  if (!product) {
    return null
  }

  return (
    <>
      {loading && <LoaderAtom>{t('loader.message')}</LoaderAtom>}

      <ToolbarMolecule
        title={product.barcode}
        actionIcon="close"
        onAction={actions.onClosePage}
        secondaryActionIcon="ic_add_icon"
        onSecondaryAction={actions.onAddNewContainer}
        thirdActionIcon={
          printerReady ? 'ic_connected_printer' : 'ic_disconnected_printer'
        }
        onThirdAction={actions.onClickPrinter}
        secondaryLabel={
          receiptType === RECEIPTS.transfers && newTransfersFlow
            ? undefined
            : t(
              product.measureUnit === 'KG'
                ? 'receipt-product-lots.counter-kg'
                : 'receipt-product-lots.counter',
              {
                counter: boxesCounter.toLocaleString('en-US'),
                total: expectedQuantity.toLocaleString('en-US')
              }
            )
        }
      />
      <FormContent>
        {(receiptType === RECEIPTS.providers || !newTransfersFlow) && (
          <CounterCardAtom
            title={t(
              product.measureUnit === MEASURE_UNITS.kg
                ? 'receipt-product-lots.counter-kg'
                : 'receipt-product-lots.counter',
              {
                counter: boxesCounter.toLocaleString('en-US'),
                total: expectedQuantity.toLocaleString('en-US')
              }
            )}
            subtitle={t(
              `receipt-product-lots.${labelsByMeasureUnit[product.measureUnit]}`
            )}
            titleSize={product.measureUnit === MEASURE_UNITS.kg ? 'h2' : 'h1'}
          ></CounterCardAtom>
        )}

        <CardImageMolecule
          imageURL={product.imgUrl}
          textLines={[
            t('receipt-order-detail.upc', { upc: product.barcode }),
            product.description,
            t('receipt-product-lots.measure', {
              measure: product.unitsOfMeasurment
            })
          ]}
        />

        <FormikProvider value={formConfig}>
          <Form onSubmit={formConfig.handleSubmit}>
            <FieldArray name="forms">
              {({ push, remove }) => (
                <>
                  <LotFormOrganism
                    formConfig={formConfig}
                    containerName={container}
                    product={product}
                    remove={remove}
                    onPrint={async (index) => {
                      await actions.printLotLabel(index)
                    }}
                  ></LotFormOrganism>
                  <SpacerAtom height={200}></SpacerAtom>
                  <BottomGroupMolecule>
                    <SecondaryButtonAtom
                      type="button"
                      onClick={async () => {
                        await actions.onAddLot(push)
                      }}
                    >
                      {t('receipt-product-lots.add-lot')}
                    </SecondaryButtonAtom>
                    <PrimaryButtonAtom
                      type="submit"
                      disabled={
                        !formConfig.isValid ||
                        (!product.isExternalToOrder &&
                          boxesCounter > product.upperTotalLimit)
                      }
                    >
                      {t('receipt-product-lots.end')}
                    </PrimaryButtonAtom>
                  </BottomGroupMolecule>
                </>
              )}
            </FieldArray>
          </Form>
        </FormikProvider>
      </FormContent>

      <MissingDialogOrganism
        receiptType={receiptType}
        dialogRef={missingDialogRef}
        missingCount={missingCounter}
        product={product}
        newTransfersFlow={newTransfersFlow}
        receivedAmount={boxesCounter}
        onMissingReasonChange={actions.onMissingReasonChange}
        coninueAction={actions.continueToMissing}
      />
    </>
  )
}

export default ProductLotsPage
